import { type UpdateRadioStatusModel } from '@/shared/api'
import { showNotificationWarningFx } from '@/shared/lib/providers'
import { HidingSpin } from '@/shared/ui'
import {
  Button,
  DatePicker,
  Flex,
  InputNumber,
  Space,
  Switch,
  Tooltip,
  Typography,
} from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useUnit } from 'effector-react'
import { type FormikProps, useFormik } from 'formik'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import { MIN_NOTIFICATION_INTERVAL } from '../../lib/constants'
import { updateRadioStatusModelSchemaRefined } from '../../lib/radio-form.schema'
import {
  $queryLoading,
  $radioHasStarted,
  $radioSettingsFormData,
  $updateLoading,
  RadioGate,
  radioSettingsUpdated,
} from '../../model/radio-form.model '
import { LiveTimer } from '../LiveTimer'
import styles from './styles.module.scss'

export function RadioForm() {
  const initialValues = useUnit($radioSettingsFormData)
  const queryLoading = useUnit($queryLoading)
  const updateLoading = useUnit($updateLoading)
  const updateSettings = useUnit(radioSettingsUpdated)
  const hasStarted = useUnit($radioHasStarted)

  const formik = useFormik<UpdateRadioStatusModel>({
    enableReinitialize: true,
    initialValues,
    validationSchema: toFormikValidationSchema(
      updateRadioStatusModelSchemaRefined,
    ),
    onSubmit: (values, { setSubmitting }) => {
      updateSettings({ data: values })
      setSubmitting(false)
    },
  })

  function handleRadioScheduledChange(
    value: boolean,
    formik: FormikProps<UpdateRadioStatusModel>,
    onSuccess: (data: UpdateRadioStatusModel) => void,
  ) {
    // Если включаем эфир и даты не установлены - устанавливаем дефолтные
    const potentialState = {
      ...formik.values,
      radioScheduled: value,
    }
    const parsed = updateRadioStatusModelSchemaRefined.safeParse(potentialState)
    if (!parsed.success) {
      // Собираем все ошибки валидации
      const errors = parsed.error.issues.map((issue) => issue.message)
      const errorMessage = errors.join('\n')

      // Устанавливаем ошибки для полей
      parsed.error.issues.forEach((issue) => {
        const path = issue.path[0]
        formik.setFieldError(String(path), issue.message)
      })

      return void showNotificationWarningFx({
        message:
          errorMessage || 'Проверьте корректность данных перед запуском эфира',
      })
    }

    void formik.setFieldValue('radioScheduled', value)
    onSuccess(potentialState)
  }

  const handleStartDateChange = (date: Dayjs | null) => {
    const currentEnd = dayjs(formik.values.endDate)
    // Обнуляем секунды при выборе даты
    const newStart = date ? date.second(0).millisecond(0).toISOString() : null

    void formik.setFieldValue('startDate', newStart)

    // Если дата окончания раньше новой даты начала - сбрасываем
    if (currentEnd && date && currentEnd.isSameOrBefore(date)) {
      void formik.setFieldValue('endDate', null)
    }
  }

  const handleEndDateChange = (date: Dayjs | null) => {
    // Обнуляем секунды при выборе даты
    void formik.setFieldValue(
      'endDate',
      date ? date.second(0).millisecond(0).toISOString() : null,
    )
  }

  // Определяем, какой таймер показывать
  const showStartTimer =
    formik.values.radioScheduled &&
    !hasStarted &&
    formik.values.startDate &&
    dayjs(formik.values.startDate).isAfter(dayjs())

  const showEndTimer =
    hasStarted &&
    formik.values.endDate &&
    dayjs(formik.values.endDate).isAfter(dayjs())

  const disabledControls = formik.values.radioScheduled || updateLoading

  return (
    <>
      <RadioGate />
      <HidingSpin spinning={queryLoading}>
        <Flex vertical gap={12}>
          <span className={styles.title}>Эфир</span>

          <Space>
            <Tooltip
              title={'Уведомление о начале эфира'}
              defaultOpen={disabledControls ? false : undefined}
            >
              <Switch
                value={formik.values.needToSendNotification}
                onChange={(value) =>
                  void formik.setFieldValue('needToSendNotification', value)
                }
                disabled={disabledControls}
              />
            </Tooltip>
            <Typography.Text
              type="secondary"
              disabled={!formik.values.needToSendNotification}
            >
              Уведомление о начале эфира за
            </Typography.Text>
            <InputNumber
              min={MIN_NOTIFICATION_INTERVAL}
              value={formik.values.sendNotificationTimeInMinutes}
              disabled={
                formik.values.radioScheduled ||
                updateLoading ||
                !formik.values.needToSendNotification
              }
              controls
              onChange={(value) =>
                void formik.setFieldValue(
                  'sendNotificationTimeInMinutes',
                  value ?? MIN_NOTIFICATION_INTERVAL,
                )
              }
            />
            <Typography.Text
              type="secondary"
              disabled={!formik.values.needToSendNotification}
            >
              мин.
            </Typography.Text>
          </Space>

          <Space align="center">
            <Space direction="vertical">
              <Typography.Text type="secondary">Начало</Typography.Text>
              <Space>
                <DatePicker
                  value={
                    formik.values.startDate
                      ? dayjs(formik.values.startDate)
                      : null
                  }
                  needConfirm={false}
                  disabled={disabledControls}
                  disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
                  onChange={handleStartDateChange}
                  placeholder=""
                  status={formik.errors.startDate ? 'error' : undefined}
                />
                <DatePicker.TimePicker
                  value={
                    formik.values.startDate
                      ? dayjs(formik.values.startDate)
                      : null
                  }
                  disabled={disabledControls}
                  needConfirm={false}
                  format="HH:mm"
                  showSecond={false}
                  onChange={(time) => {
                    if (!time) return

                    const current = formik.values.startDate
                      ? dayjs(formik.values.startDate)
                      : dayjs()
                    const withTime = current
                      .set('hour', time.hour())
                      .set('minute', time.minute())
                      .set('second', 0)
                      .set('millisecond', 0)

                    void formik.setFieldValue(
                      'startDate',
                      withTime.toISOString(),
                    )
                  }}
                  placeholder=""
                  status={formik.errors.startDate ? 'error' : undefined}
                />
                <Typography.Text type="secondary">—</Typography.Text>
              </Space>
            </Space>

            <Space direction="vertical">
              <Typography.Text type="secondary">Конец</Typography.Text>
              <Space>
                <DatePicker
                  value={
                    formik.values.endDate ? dayjs(formik.values.endDate) : null
                  }
                  disabled={disabledControls}
                  needConfirm={false}
                  disabledDate={(d) =>
                    formik.values.startDate
                      ? d.isBefore(
                          dayjs(formik.values.startDate).startOf('day'),
                        )
                      : d.isBefore(dayjs().startOf('day'))
                  }
                  onChange={handleEndDateChange}
                  placeholder=""
                  status={formik.errors.endDate ? 'error' : undefined}
                />
                <DatePicker.TimePicker
                  value={
                    formik.values.endDate ? dayjs(formik.values.endDate) : null
                  }
                  disabled={disabledControls}
                  needConfirm={false}
                  format="HH:mm"
                  showSecond={false}
                  onChange={(time) => {
                    if (!time) return

                    const current = formik.values.endDate
                      ? dayjs(formik.values.endDate)
                      : dayjs()
                    const withTime = current
                      .set('hour', time.hour())
                      .set('minute', time.minute())
                      .set('second', 0)
                      .set('millisecond', 0)

                    void formik.setFieldValue('endDate', withTime.toISOString())
                  }}
                  placeholder=""
                  status={formik.errors.endDate ? 'error' : undefined}
                />
              </Space>
            </Space>
          </Space>

          <Space>
            <Button
              loading={updateLoading}
              onClick={() => {
                const value = !formik.values.radioScheduled
                handleRadioScheduledChange(value, formik, (data) => {
                  updateSettings({ data })
                })
              }}
              variant="solid"
              type="primary"
              danger={formik.values.radioScheduled || hasStarted}
            >
              {hasStarted
                ? 'Завершить'
                : formik.values.radioScheduled
                  ? 'Отменить'
                  : 'Запланировать'}
            </Button>
            {showStartTimer && formik.values.startDate && (
              <LiveTimer
                targetDate={formik.values.startDate}
                label="До начала эфира"
              />
            )}
            {showEndTimer && formik.values.endDate && (
              <LiveTimer
                targetDate={formik.values.endDate}
                label="До конца эфира"
                isEnd
              />
            )}
          </Space>
        </Flex>
      </HidingSpin>
    </>
  )
}
